import React, { useState, useEffect } from "react";
import { TableData } from "../types";

interface RightSectionProps {
  keywordData: { keyword: string; count: number; density: string }[];
  primaryKeyword: string;
  secondaryKeyword1: string;
  secondaryKeyword2: string;
  primaryKeywordData: { count: number; density: string };
  secondaryKeyword1Data: { count: number; density: string };
  secondaryKeyword2Data: { count: number; density: string };
  keywordPlacementData: TableData[];
  hasFetched: boolean;
  filter: "single" | "short" | "long";
  onFilterChange: (filter: "single" | "short" | "long") => void;
  loading: boolean;
  activeTab: "text" | "url";
}

const RightSection: React.FC<RightSectionProps> = ({
  keywordData,
  primaryKeyword,
  secondaryKeyword1,
  secondaryKeyword2,
  primaryKeywordData,
  secondaryKeyword1Data,
  secondaryKeyword2Data,
  keywordPlacementData,
  hasFetched,
  filter,
  onFilterChange,
  loading,
  activeTab,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isUrlAllKeywords = activeTab === "url" && !primaryKeyword.trim() && !secondaryKeyword1.trim() && !secondaryKeyword2.trim();

  const handleExcelDownload = () => {
    let csv = "";
    let filename = "";

    if (activeTab === "text" || isUrlAllKeywords) {
      const headers = ["S.No", "Keyword", "Occurrence", "Usage (%)"];
      const rows = keywordData.slice(0, 10).map((item, index) => [
        index + 1,
        `"${item.keyword}"`,
        item.count,
        item.density,
      ]);

      csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
      filename = activeTab === "text" ? "keyword-density-text.csv" : "keyword-density-url-all.csv";
    } else {
      const headers = [
        "Keyword",
        "Intro",
        "Body",
        "H1",
        "URL Slug",
        "Meta Description",
        "Meta Title",
      ];

      const rows = keywordPlacementData.map((item) => [
        `"${item.keyword}"`,
        item.intro,
        item.body,
        item.h1,
        item.slug,
        item.desc,
        item.title,
      ]);

      csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
      filename = "keyword-density-url.csv";
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const canDownload =
    (activeTab === "text" || isUrlAllKeywords)
      ? keywordData.length > 0
      : keywordPlacementData.length > 0;

  const showAllKeywordsAnalysis =
  activeTab === "text" || activeTab === "url";
  const topKeywords = keywordData.slice(0, 10); // Limit to top 10

  const getOptimizationMessage = () => {
    if (topKeywords.length === 0) return "";
    const maxDensity = Math.max(...topKeywords.map(k => parseFloat(k.density.replace('%', ''))));
    if (maxDensity > 3) {
      return "Warning: Potential keyword stuffing detected.";
    } else {
      return "Great! No signs of keyword stuffing / over-optimization.";
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1 0 auto",
        border: "1px solid #A8DE66",
        borderRadius: "8px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        height: "100%",
        padding: "1rem",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <h5 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
          Keyword Density Results
        </h5>

        <button
          onClick={handleExcelDownload}
          disabled={!canDownload}
          style={{
            padding: "0.45rem 0.9rem",
            backgroundColor: canDownload ? "#6B9A30" : "#9ccc7a",
            color: "#fff",
            border: "1px solid #6B9A30",
            borderRadius: "4px",
            cursor: canDownload ? "pointer" : "not-allowed",
            fontSize: "0.85rem",
            opacity: canDownload ? 1 : 0.6,
          }}
        >
          Download Excel
        </button>

      </div>

      {activeTab === "text" && (
        <>
          <h6 style={{ marginBottom: "0.5rem", color: "#555" }}>
            Keyword Density Overview
          </h6>
          <div
            style={{
              overflowX: "auto",
              marginBottom: "1rem",
              maxHeight: "200px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#6B9A30", color: "white" }}>
                <tr>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    S.No
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Keyword
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Occurrence
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Usage
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    1
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {primaryKeyword || "-"}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {primaryKeywordData.count}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {primaryKeywordData.density}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    2
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword1 || "-"}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword1Data.count}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword1Data.density}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    3
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword2 || "-"}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword2Data.count}
                  </td>
                  <td
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {secondaryKeyword2Data.density}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Show Keyword Placement Density for URL Input */}
      {activeTab === "url" && (
        <>
          <h6 style={{ marginBottom: "0.5rem", marginTop: "1.5rem", color: "#555" }}>
            Keyword Placement Density
          </h6>
          <div
            style={{
              overflowX: "auto",
              marginBottom: "1rem",
              maxHeight: "200px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#6B9A30", color: "white" }}>
                <tr>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Keyword
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Intro
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Body
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    H1 tag
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    URL Slug
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Meta Desc tag
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Meta Title tag
                  </th>
                </tr>
              </thead>
              <tbody>
                {keywordPlacementData.length > 0 ? (
                  keywordPlacementData.map((item, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.keyword}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.intro}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.body}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.h1}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.slug}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.desc}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.title}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        border: "1px solid #A8DE66",
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {hasFetched ? "No data available" : "Please check keyword density to view results"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Show All Keywords Analysis for Text Input or URL Input without keywords */}
      {showAllKeywordsAnalysis && (
        <>
          <h6 style={{ marginBottom: "0.5rem", marginTop: "1.5rem", color: "#555" }}>
            {filter === "single" ? "One word phrases" : filter === "short" ? "Two word phrases" : "Three word phrases"}
          </h6>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "0.5rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => onFilterChange("single")}
              style={{
                padding: isMobile ? "0.5rem 1rem" : "0.75rem 1rem",
                backgroundColor: filter === "single" ? "#8CC540" : "#fff",
                color: filter === "single" ? "white" : "#333",
                border: "1px solid #A8DE66",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1rem",
                flex: 1,
                minWidth: "80px",
                whiteSpace: "nowrap",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "single" ? "#6B9A30" : "#e9ecef")
              }
              onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "single" ? "#8CC540" : "#fff")
              }
            >
              {isMobile ? "Single" : "Single Words"}
            </button>
            <button
              onClick={() => onFilterChange("short")}
              style={{
                padding: isMobile ? "0.5rem 1rem" : "0.75rem 1rem",
                backgroundColor: filter === "short" ? "#8CC540" : "#fff",
                color: filter === "short" ? "white" : "#333",
                border: "1px solid #A8DE66",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1rem",
                flex: 1,
                minWidth: "80px",
                whiteSpace: "nowrap",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "short" ? "#6B9A30" : "#e9ecef")
              }
              onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "short" ? "#8CC540" : "#fff")
              }
            >
              {isMobile ? "Short" : "Short-Tail (2 Words)"}
            </button>
            <button
              onClick={() => onFilterChange("long")}
              style={{
                padding: isMobile ? "0.5rem 1rem" : "0.75rem 1rem",
                backgroundColor: filter === "long" ? "#8CC540" : "#fff",
                color: filter === "long" ? "white" : "#333",
                border: "1px solid #A8DE66",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1rem",
                flex: 1,
                minWidth: "80px",
                whiteSpace: "nowrap",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "long" ? "#6B9A30" : "#e9ecef")
              }
              onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                filter === "long" ? "#8CC540" : "#fff")
              }
            >
              {isMobile ? "Long" : "Long-Tail (3 Words)"}
            </button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#6B9A30", color: "white" }}>
                <tr>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Result
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Keyword
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Frequency
                  </th>
                  <th
                    style={{
                      border: "1px solid #A8DE66",
                      padding: "0.75rem",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    Density
                  </th>
                  
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        border: "1px solid #A8DE66",
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      Loading keywords...
                    </td>
                  </tr>
                ) : topKeywords.length > 0 ? (
                  topKeywords.map((item, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.keyword}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.count}
                      </td>
                      <td
                        style={{
                          border: "1px solid #A8DE66",
                          padding: "0.75rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {item.density}
                      </td>
                      
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        border: "1px solid #A8DE66",
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      No keywords available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* {topKeywords.length > 0 && (
            <div style={{ marginTop: "0.5rem", padding: "0.5rem", backgroundColor: "#d4edda", borderRadius: "4px", fontSize: "0.85rem", color: "#155724" }}>
              {getOptimizationMessage()}
            </div>
          )} */}
        </>
      )}
    </div>
  );
};

export default RightSection;